import React, { useEffect, useState } from "react";
import { Box, Typography, Pagination } from "@mui/material";
import { useRole } from "../../context/RoleContext";
import moment from "moment";
import DataTable from "../../common/DataTable";
import Breadcrumbs from "../../common/Breadcrumbs";
import {
  columnStyles,
  paginationCaptionStyle,
  paginationContainerStyle,
  preBookingsTableContainerSx,
} from "./PreBookings.styles";
import SortBy from "../../common/filters/SortBy";
import SearchBar from "../../common/filters/SearchBar";
import CustomDateRangePicker from "../../pages/selller/dashboards/components/custom-date-range";
import CustomDropdown from "../../common/CustomDropdown";
import {
  getAllPreorders,
  getPreordersByCustomerEmail,
  updatePrebookingStatus,
} from "../../api/preorderAPI";
import { Pagination as PaginationType } from "../../types/Order";
import { getUserEmailFromLocalStorage } from "../../utils/localStorageUtils";
import colors from "../../theme/Colours";
import { exportToExcel } from "../../utils/exportToExcel";
import CloudDownloadIcon from "../../assets/download-cloud-02.svg";
import {
  downloadCsvButtonStyle,
  downloadIconStyle,
  SortBYContainerStyle,
} from "../customerManagement/DisplayCustomers.styles";
import {
  FiltersSection,
  FiltersRight,
  StyledCard,
  OrderStatusText,
} from "../allOrdersManagement/OrderDetails.styles";
import StatusFilter from "../../common/filters/StatusFilter";
import { useOutsideClose } from "../../hooks/useOutsideClose";

type Prebooking = {
  id: string;
  bookingDate: string;
  bookingID?: string;
  customerName?: string;
  productName: string;
  quantity: { unit: number; multiplier: number };
  amount: number;
  status: "PLACED" | "CANCELLED" | "MOVED_TO_ORDERS";
};

const sortOptions = ["Newest", "Oldest"];

const statusColors: Record<Prebooking["status"], string> = {
  PLACED: colors.preBookingStatusOrange,
  CANCELLED: colors.redText,
  MOVED_TO_ORDERS: colors.preBookingStatusGreen,
};

const Prebookings: React.FC = () => {
  const { role } = useRole();
  const [prebookings, setPrebookings] = useState<Prebooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSort, setSelectedSort] = useState(sortOptions[0]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusEdit, setStatusEdit] = useState<
    Record<string, Prebooking["status"]>
  >({});
  const rowsPerPage = 10;
  const isSeller =
    role === "maltora-seller" ||
    location.pathname.startsWith("/maltora-seller");
  const [pagination, setPagination] = useState<PaginationType>({
    totalRecords: 0,
    totalPages: 1,
    currentPage: 1,
    limit: 10,
  });
  const emailId = getUserEmailFromLocalStorage() || "";
  const [from, setFrom] = useState<Date | undefined>(undefined);
  const [to, setTo] = useState<Date | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [openFilter, setOpenFilter] = React.useState<
    "sort" | "date" | "status" | null
  >(null);
  const filterContainerRef = React.useRef<HTMLDivElement>(null);

  useOutsideClose(filterContainerRef, openFilter !== null, () =>
    setOpenFilter(null)
  );

  const fetchPreorders = async () => {
    try {
      let fetchedOrders;
      const sortOrderValue = selectedSort === "Oldest" ? "ASC" : "DESC";
      if (isSeller) {
        fetchedOrders = await getAllPreorders(
          pagination.currentPage,
          pagination.limit,
          from ? from.toISOString() : null,
          to ? to.toISOString() : null,
          sortOrderValue,
          debouncedSearchTerm,
          statusFilter || null
        );
      } else {
        fetchedOrders = await getPreordersByCustomerEmail(
          emailId,
          pagination.currentPage,
          pagination.limit,
          sortOrderValue,
          debouncedSearchTerm
        );
      }
      setPagination({
        totalRecords: fetchedOrders.data.pagination.totalItems,
        totalPages: fetchedOrders.data.pagination.totalPages,
        currentPage: fetchedOrders.data.pagination.currentPage,
        limit: fetchedOrders.data.pagination.pageSize,
      });
      setPrebookings(
        fetchedOrders.data.products.map((item: any) => ({
          id: item.preorderId,
          bookingDate: item.createdAt,
          bookingID: item.preorderId,
          customerName: item.customerName,
          productName: item.name,
          quantity: {
            unit: Number(item.quantity),
            multiplier: Number(item.units),
          },
          amount:
            (item.priceInCents / 100) *
            Number(item.quantity) *
            Number(item.units),
          status: item.status,
          productId: item.id,
        }))
      );
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!emailId) {
      console.warn("No email ID found in userRole key.");
    }
    fetchPreorders();
  }, [
    pagination.currentPage,
    pagination.limit,
    from,
    to,
    selectedSort,
    debouncedSearchTerm,
    statusFilter,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [statusFilter]);

  const generateCSV = async () => {
    try {
      const sortOrderValue = selectedSort === "Oldest" ? "ASC" : "DESC";
      let allPrebookings;

      if (isSeller) {
        allPrebookings = await getAllPreorders(
          1,
          10000,
          from ? from.toISOString() : null,
          to ? to.toISOString() : null,
          sortOrderValue,
          debouncedSearchTerm,
          statusFilter || null
        );
      } else {
        allPrebookings = await getPreordersByCustomerEmail(
          emailId,
          1,
          10000,
          sortOrderValue,
          debouncedSearchTerm
        );
      }

      const allData = allPrebookings.data.products;

      if (!allData.length) {
        console.error("No prebookings to export");
        return;
      }

      const excelData = allData.map((prebooking: any) => {
        if (isSeller) {
          return {
            "Booking Date": moment(prebooking.createdAt).format(
              "DD MMM YY hh:mm:ss A"
            ),
            "Booking ID": prebooking.preorderId,
            "Customer Name": prebooking.customerName,
            "Product Name": prebooking.name,
            Quantity: `${Number(prebooking.quantity)}*${Number(prebooking.units)}=${Number(prebooking.quantity) * Number(prebooking.units)}`,
            Status: prebooking.status,
          };
        } else {
          return {
            "Booking Date": moment(prebooking.createdAt).format(
              "DD MMM YY hh:mm:ss A"
            ),
            "Product Name": prebooking.name,
            Quantity: `${Number(prebooking.quantity)}*${Number(prebooking.units)}=${Number(prebooking.quantity) * Number(prebooking.units)}`,
            Amount:
              (prebooking.priceInCents / 100) *
              Number(prebooking.quantity) *
              Number(prebooking.units),
            Status: prebooking.status,
          };
        }
      });
      await exportToExcel(excelData, "prebookings.xlsx");
    } catch (error) {
      console.error("Error generating CSV:", error);
    }
  };

  const statusOptions1 = [
    { value: "", label: "All" },
    { value: "PLACED", label: "Placed" },
    { value: "MOVED_TO_ORDERS", label: "Moved to Orders" },
    { value: "CANCELLED", label: "Cancelled" },
  ];

  const handleChangePage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusChange = async (
    prebookingId: string,
    newStatus: Prebooking["status"]
  ) => {
    try {
      setStatusEdit((prev) => ({ ...prev, [prebookingId]: newStatus }));
      setPrebookings((prev) =>
        prev.map((item) =>
          item.id === prebookingId ? { ...item, status: newStatus } : item
        )
      );
      await updatePrebookingStatus({ id: prebookingId, status: newStatus });
    } catch (error) {
      console.error("Error updating prebooking status", error);
    }
  };

  const statusLabels: Record<string, string> = {
    MOVED_TO_ORDERS: "Moved To Orders",
    PLACED: "Placed",
    CANCELLED: "Cancelled",
  };

  const baseColumns: any[] = [
    {
      label: "Booking Date",
      field: "bookingDate",
      ...columnStyles,
      align: "left" as const,
      renderCell: (row: Prebooking) => (
        <Typography sx={columnStyles.cellStyle}>
          {isSeller
            ? moment(row.bookingDate).format("DD MMM YY")
            : moment(row.bookingDate).format("DD MMM YY hh:mm:ss A")}
        </Typography>
      ),
    },
    {
      label: "Product Name",
      field: "productName",
      ...columnStyles,
      align: "center" as const,
      renderCell: (row: Prebooking) => (
        <Typography sx={columnStyles.cellStyle}>{row.productName}</Typography>
      ),
    },
    {
      label: "Quantity",
      field: "quantity",
      ...columnStyles,
      align: "center" as const,
      renderCell: (row: Prebooking) => (
        <Typography sx={columnStyles.cellStyle}>
          {`${row.quantity.unit}*${row.quantity.multiplier}=${
            row.quantity.unit * row.quantity.multiplier
          }`}
        </Typography>
      ),
    },
  ];

  const columns = [...baseColumns];

  if (isSeller) {
    columns.splice(1, 0, {
      label: "Booking ID",
      field: "bookingID",
      ...columnStyles,
      align: "center" as const,
      renderCell: (row: Prebooking) => (
        <Typography
          sx={{
            ...columnStyles.cellStyle,
            color: "#378eee",
            cursor: "pointer",
          }}
        >
          {row.bookingID}
        </Typography>
      ),
    });

    columns.splice(2, 0, {
      label: "Customer Name",
      field: "customerName",
      ...columnStyles,
      align: "center" as const,
      renderCell: (row: Prebooking) => (
        <Typography sx={columnStyles.cellStyle}>{row.customerName}</Typography>
      ),
    });

    columns.push({
      label: "Status",
      field: "status",
      ...columnStyles,
      align: "center" as const,
      renderCell: (row: Prebooking) => (
        <CustomDropdown
          label="Select status"
          value={statusEdit[row.id] || row.status}
          options={statusOptions1}
          onChange={(newValue) => {
            setStatusEdit((prev) => ({
              ...prev,
              [row.id]: newValue as Prebooking["status"],
            }));
            handleStatusChange(row.id, newValue as Prebooking["status"]);
          }}
          customStyles={{
            button: {
              fontWeight: 500,
              color: statusColors[statusEdit[row.id] || row.status],
              minWidth: 170,
              border: "none",
              boxShadow: "none",
            },
            menu: {
              minWidth: 170,
              borderRadius: "6px",
            },
            option: {
              fontSize: "12px",
            },
          }}
        />
      ),
    });
  } else {
    columns.push(
      {
        label: "Amount",
        field: "amount",
        ...columnStyles,
        align: "center" as const,
        renderCell: (row: Prebooking) => (
          <Typography sx={columnStyles.cellStyle}>
            {row.amount.toLocaleString()}
          </Typography>
        ),
      },
      {
        label: "Status",
        field: "status",
        ...columnStyles,
        align: "center" as const,
        renderCell: (row: Prebooking) => (
          <OrderStatusText sx={{ color: statusColors[row.status] }}>
            {statusLabels[row.status] || row.status}
          </OrderStatusText>
        ),
      }
    );
  }

  const { currentPage, limit, totalPages, totalRecords } = pagination;
  const startIndex = (+currentPage - 1) * +limit;
  const endIndex = Math.min(startIndex + +limit, +totalRecords);

  return (
    <Box>
      <Breadcrumbs
        paths={["Home", isSeller ? "All Prebookings" : "My Prebookings"]}
        links={["/maltora-customer/home", "/maltora-customer/my-prebookings"]}
      />
      <FiltersSection>
        <SearchBar searchTerm={searchTerm} handleSearch={handleSearch} />
        <Box ref={filterContainerRef}>
          <FiltersRight>
            {isSeller && (
              <>
                <Box>
                  <CustomDateRangePicker
                    fromDate={from}
                    toDate={to}
                    setFromDate={setFrom}
                    setToDate={setTo}
                    placeholder="Select date range"
                    disableFuture
                    showReset
                    sx={{
                      height: "38px",
                      width: "220px",
                    }}
                  />
                </Box>
                <StatusFilter
                  selected={statusFilter}
                  options={statusOptions1}
                  setSelected={(value) => {
                    setStatusFilter(value);
                    setOpenFilter(null);
                  }}
                  variant="seller"
                  isOpen={openFilter === "status"}
                  onToggle={(open) => setOpenFilter(open ? "status" : null)}
                />
              </>
            )}
            <SortBy
              containerSx={SortBYContainerStyle}
              selected={selectedSort}
              options={sortOptions}
              setSelected={setSelectedSort}
              isOpen={openFilter === "sort"}
              onToggle={(open) => setOpenFilter(open ? "sort" : null)}
              variant="seller"
              hideOptions
            />
            <button
              onClick={generateCSV}
              style={downloadCsvButtonStyle as React.CSSProperties}
            >
              <img
                src={CloudDownloadIcon}
                alt="Download"
                style={downloadIconStyle}
              />
              Download
            </button>
          </FiltersRight>
        </Box>
      </FiltersSection>
      {loading ? (
        <Typography variant="h6" align="center">
          Loading prebookings...
        </Typography>
      ) : (
        <StyledCard sx={{ p: 4, overflow: "visible" }}>
          <DataTable
            columns={columns}
            data={prebookings}
            emptyStateMessage="No prebookings available."
            rowsPerPage={rowsPerPage}
            rowStyle={(index) => ({
              borderBottom:
                index === prebookings.length - 1
                  ? "none"
                  : "1px solid rgba(224, 224, 224, 1)",
            })}
            tableContainerSx={preBookingsTableContainerSx}
          />
        </StyledCard>
      )}
      <Box sx={paginationContainerStyle}>
        <Typography variant="subtitle2" sx={paginationCaptionStyle}>
          Showing data {startIndex + 1} to {endIndex} of {totalRecords} entries
        </Typography>
        <Pagination
          count={totalPages}
          page={+currentPage}
          onChange={(event, value) => handleChangePage(value)}
          shape="rounded"
          variant="outlined"
        />
      </Box>
    </Box>
  );
};

export default Prebookings;
